/**
 * D2 · প্রদানকারী তুলনা — `/offering/:id/compare`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame D2 (360 × 680).
 *
 * Frame note: "Unavailable providers stay visible under their own heading with the
 * reason and the next opening. Hiding them makes a thin market look broken rather than
 * busy." So the unavailable block is not a filter and not collapsed — it is drawn, and
 * the SERVER returns those rows rather than the client keeping what it was told to hide.
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * `GET /v1/offerings/:id/compare?sort=`. The sort chips were local state that reordered
 * nothing; they now ask the server for the ordering, and the server says which chip is
 * selected. Which of three orderings a market is read in is a decision about the market,
 * not about a component's `useState`.
 */
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Chip, IconButton } from '@oprappo/ui';
import type { CompareResponse, CompareSort } from '@oprappo/contracts';
import { Frame, SAND_FRAME } from './chrome.js';
import { Loaded, commerce, useResource } from './commerce.js';

/**
 * `width:44px;height:44px;background:#EDEAE1;border:1px dashed #C4BFB2` — 46 × 46 as
 * drawn, because the canvas is content-box everywhere outside a frame box. `box-content`
 * restores that against this app's border-box reset.
 */
function Thumb(): JSX.Element {
  return (
    <div className="w-[44px] h-[44px] box-content bg-sand-deep border border-dashed border-border-warm flex-none" />
  );
}

export function D2CompareView({
  compare,
  onSort,
}: {
  compare: CompareResponse;
  onSort?: (sort: CompareSort) => void;
}): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={SAND_FRAME}>
      <div className="px-[16px] pt-[16px]">
        <div className="flex gap-[12px] items-center mb-[14px]">
          <IconButton control="back" size={20} onClick={() => navigate(-1)} />
          <div className="font-anek font-semibold text-[19px] leading-[1.75] text-ink">
            {compare.title_bn}
          </div>
        </div>
        {/*
         * Drawn `padding:8px 14px`; `@oprappo/ui`'s § 03 Chip is `8px 13px`. One pixel
         * per side, on the component the design system calls canonical — the component
         * is used and the difference reported rather than a second chip being written.
         */}
        <div className="flex gap-[8px] pb-[14px] overflow-hidden">
          {compare.sorts.map((s) => (
            // The frame draws the unselected chips on `background:#fff`; § 03's Chip is
            // transparent because the design system draws it on a white surface. The
            // white ground is supplied behind the chip rather than fought for with a
            // competing utility class. Reported.
            <span key={s.id} className="bg-white rounded-pill inline-flex flex-none">
              <Chip
                labelBn={s.label_bn}
                selected={s.id === compare.selected_sort}
                onClick={() => onSort?.(s.id)}
              />
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1 px-[16px] oprappo-scroll">
        <div className="flex flex-col gap-[11px]">
          {compare.available.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => navigate(`/offering/${p.id}`)}
              className="bg-white border border-border p-[13px] text-left rounded-none block w-full"
            >
              <div className="flex gap-[12px] items-start">
                <Thumb />
                <div className="flex-1">
                  <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                    {p.name_bn}
                  </div>
                  <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                    {p.meta_bn}
                  </div>
                </div>
                <div className="text-right flex-none">
                  <div className="font-manrope font-semibold text-[16px] leading-[1] text-ink tabular-nums">
                    {p.price.display}
                  </div>
                  <div
                    className={
                      'font-anek font-medium text-[13px] leading-[1.75] mt-[4px] ' +
                      (p.eta_tone === 'success' ? 'text-success' : 'text-muted')
                    }
                  >
                    {p.eta_bn}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-[18px] pt-[14px] border-t border-border">
          <div className="font-anek font-semibold text-[14px] leading-[1.75] text-muted mb-[10px]">
            {compare.unavailable_heading_bn}
          </div>
          {compare.unavailable.map((p) => (
            <div key={p.id} className="bg-white border border-border p-[13px] opacity-[.62]">
              <div className="flex gap-[12px] items-center">
                <Thumb />
                <div className="flex-1">
                  <div className="font-anek font-semibold text-[15.5px] leading-[1.75] text-ink">
                    {p.name_bn}
                  </div>
                  <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                    {p.reason_bn}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

export function D2Compare(): JSX.Element {
  const { id } = useParams();
  const offeringId = id ?? '';
  const [sort, setSort] = useState<CompareSort | null>(null);
  const load = useCallback(
    (signal: AbortSignal) => commerce.compare(offeringId, sort, signal),
    [offeringId, sort],
  );
  const [resource] = useResource(load, `compare:${offeringId}:${sort ?? ''}`);

  return (
    <Loaded resource={resource}>
      {(compare) => <D2CompareView compare={compare} onSort={setSort} />}
    </Loaded>
  );
}
